/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { Framework, frameworks, Group, Standard, standards } from '@g17eco/core';
import type { SurveyScope } from '@g17eco/types/surveyCommon';
import type { Tags, UniversalTrackerPlain } from '../types/universalTracker';
import { naturalSort } from '@utils/index';
import { UsedScopes } from '@api/organization';

// No Questions Available for these codes
const excludedFrameworkCodes = ['cdsb-req11'];
export const EESG_TYPE = 'eesg_2024';

export const getStandards = (surveyScope?: SurveyScope) =>
  Object.values(standards).filter((s) => (s.hidden ? surveyScope?.standards?.includes(s.code) : true));
export const getFrameworks = (surveyScope?: SurveyScope) =>
  Object.values(frameworks).filter((s) => (s.hidden ? surveyScope?.frameworks?.includes(s.code) : true));

export interface FrameworkOption {
  code: string;
  value: string;
  searchString: string;
  label: JSX.Element;
  scopeType: 'frameworks';
  frameworkCode: string;
}

export interface PackOption extends Omit<FrameworkOption, 'code' | 'scopeType' | 'frameworkCode'> {
  scopeType: 'standards' | 'frameworks' | 'custom';
}

interface GetPackOptions {
  showShortName?: boolean
}

export const getFrameworksOptions = (surveyScope?: SurveyScope) => {
  const getOption = (f: Group, parentGroup: Group) => {
    return (
      <span>
        <img src={f.src ?? parentGroup.src} alt={f.name} height='26px' className='mr-2' />
        <span>{f.name}</span>
      </span>
    );
  };
  const frameworksOptions: FrameworkOption[] = [];
  getFrameworks(surveyScope).forEach((framework: Framework) => {
    framework.subgroups?.forEach((f: Group) => {
      if (!excludedFrameworkCodes.includes(f.code)) {
        frameworksOptions.push({
          value: f.code,
          searchString: `${framework.code} ${f.name ?? ''}`,
          label: getOption(f, framework),
          scopeType: 'frameworks',
          frameworkCode: framework.code
        } as FrameworkOption);
      }
    });
  });
  return frameworksOptions;
};

export const getRootFrameworksOptions = (options?: GetPackOptions): PackOption[] => {
  return [
    ...getFrameworks().map((framework: Framework) => ({
      value: framework.code,
      searchString: `${framework.code} ${framework.name}`,
      label: (
        <span>
          <img src={framework.src} alt={framework.name} height='26px' className='mr-2' />
          <span>{options?.showShortName ? framework.shortName : framework.name}</span>
        </span>
      ),
      scopeType: 'frameworks',
    }) as PackOption),
  ];
};

export const getStandardsOptions = (surveyScope?: SurveyScope, options?: GetPackOptions): PackOption[] => {
  return [
    ...getStandards(surveyScope).map((standard: Standard) => ({
      value: standard.code,
      searchString: `${standard.code} ${standard.name ?? ''}`,
      label: (
        <span>
          <img src={standard.src} alt={standard.name} height='26px' className='mr-2' />
          <span>{options?.showShortName ? standard.shortName : standard.name}</span>
        </span>
      ),
      scopeType: 'standards',
    }) as PackOption),
  ];
};

export const getUsedStandardAndFrameworkOptions = (scope: UsedScopes): PackOption[] => {
  const standardOptions = Object.values(standards).filter((s) => Object.keys(scope.standards).includes(s.code));
  const frameworkOptions = Object.values(frameworks).filter((s) => Object.keys(scope.frameworks).includes(s.code));
  const sortedOptions = [...standardOptions, ...frameworkOptions].sort((a, b) => naturalSort(a.name, b.name));
  return sortedOptions.map(
    (op: Standard | Framework) =>
      ({
        value: op.code,
        searchString: `${op.code} ${op.name ?? ''}`,
        label: (
          <span>
            <img src={op.src} alt={op.name} height='26px' className='mr-2' />
            <span>{op.name}</span>
          </span>
        ),
        scopeType: standards[op.code] ? 'standards' : 'frameworks',
      } as PackOption)
  );
};

export const getStandardsFrameworksValues = () =>
  [...getStandards(), ...getFrameworks()].map((item) => ({ label: item.name, value: item.code }));

export const SURVEY_PACKS = [...getStandardsOptions(), ...getRootFrameworksOptions()];
export const SURVEY_PACKS_SHORT_NAME = [...getStandardsOptions(undefined, { showShortName: true }), ...getRootFrameworksOptions({ showShortName: true })];

export const getSortedVersions = (versionGroupCode: string | undefined): (Standard | Framework)[] => {
  if (!versionGroupCode) {
    return [];
  }
  const versions = [...Object.values(standards), ...Object.values(frameworks)].filter(p => p.versionGroupCode === versionGroupCode);
  return versions.sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
}

export const getUtrFrameworkNames = (utr?: Pick<UniversalTrackerPlain, 'tags'>) => {
  const tags = utr?.tags;
  if (!tags) {
    return [];
  }

  return Object.values(frameworks)
    .filter((framework) => tags[framework.code as keyof Tags]?.length)
    .map(({ name }) => name);
};

export const getUtrStandardNames = (utr?: Pick<UniversalTrackerPlain, 'type' | 'name' | 'alternatives'>) => {
  if (!utr) {
    return [];
  }

  const names: string[] = [];
  if (standards[utr.type]) {
    names.push(standards[utr.type].name);
  }

  if (utr.alternatives) {
    Object.keys(utr.alternatives).forEach((key) => {
      if (standards[key]) {
        names.push(standards[key].name);
      }
    });
  }

  return Array.from(new Set(names));
};
